import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { BUREAU_ENUMS } from 'pages/RedesignDashboard/constants';
import Label from 'components/form/Label/Label';
import React from 'react';
import { Image } from 'components/Image/Image';
import classnames from 'classnames';
import {
  getLastSixMonthsFirstCharacter,
  getMonthAndYearFromDate,
} from 'helpers/dateHelper';
import { monthsToYears } from 'helpers/dateHelper';
import FactorUpdateButton from 'components/FactorUpdateButton/index';
import FactorAddDataButton from 'components/FactorAddDataButton/index';

const { ALL, TRANS_UNION, EXPERIAN, EQUIFAX } = BUREAU_ENUMS;

const PaymentHistoryItem = props => {
  const { label, success, refused, showTilesRow, bureau } = props;
  const isTransUnionBureau = bureau?.toLowerCase() === 'transunion';

  return (
    <FlexContainer className={'flex-column align-items-center format100'}>
      {showTilesRow && (
        <Label
          type="Lato-Bold"
          className={'system_neutral_n250_text bold text-10 margb4 padlr2'}
        >
          {label}
        </Label>
      )}
      {!showTilesRow && (
        <>
          {success && <Image className={'checkMarkBlue'} />}
          {refused && <Image className={'closeSmallRed'} />}
          {!success && !refused && isTransUnionBureau && (
            <Image className={'minusGrey'} />
          )}
        </>
      )}
    </FlexContainer>
  );
};

const SixMonthPaymentHistoryRow = props => {
  const { bureau, showTilesRow, data, showRow, unupdated, linkClassName } =
    props;
  const currentDate = new Date();
  const monthsTiles = getLastSixMonthsFirstCharacter();
  const statesForLastSixMonths = monthsTiles.map(monthName => {
    const { month, year } = getMonthAndYearFromDate(
      monthName,
      currentDate.getFullYear(),
    );
    const key = `${month}-${year}`;
    const state = data && data[key] ? data[key] : 'N/A';
    return { month: monthName.charAt(0), state: state };
  });

  if (showRow) {
    return (
      <FlexContainer
        className={classnames(
          'format100 align-items-center',
          {
            bgGray005: unupdated,
          },
          linkClassName,
        )}
      >
        <Label
          type="Lato-Bold"
          className={
            'system_neutral_n250_text align-self-end text-12 margl4 margtb4'
          }
          style={{ width: '110px' }}
        >
          {bureau}
        </Label>
        <FlexContainer className={'format100'}>
          {statesForLastSixMonths.map(item => (
            <PaymentHistoryItem
              bureau={bureau}
              key={item?.month}
              label={item?.month}
              showTilesRow={showTilesRow}
              success={item?.state === 'CURRENT'}
              refused={
                item?.state !== 'CURRENT' &&
                item?.state !== 'N/A' &&
                item?.state !== 'UNKNOWN'
              }
            />
          ))}
        </FlexContainer>
      </FlexContainer>
    );
  }
  return <></>;
};

const BureauPillComponent = props => {
  const { className, label } = props;
  return (
    <span
      className={classnames(
        'bold align-self-start text-10 padlr8 padtb2 margtb4 border-rad8px',
        className,
      )}
    >
      {label}
    </span>
  );
};

const BureauPillRowConponent = props => {
  const { bureauAnnotation, isDownloadAvailable, showRow } = props;
  if (showRow) {
    return (
      <>
        {bureauAnnotation?.current && !isDownloadAvailable && (
          <BureauPillComponent className={'bgNeutral025'} label={'CURRENT'} />
        )}
        {bureauAnnotation?.delinquent && !isDownloadAvailable && (
          <BureauPillComponent
            className={'bgYellow025 fontYellow500'}
            label={'LATE'}
          />
        )}
        {bureauAnnotation?.derogatory && !isDownloadAvailable && (
          <BureauPillComponent
            className={'bgOrange025 fontRed4'}
            label={'DEROGATORY'}
          />
        )}
      </>
    );
  } else {
    return <></>;
  }
};

export const SixMonthPaymentHistoryTable = props => {
  const {
    parsedTradeline,
    selectedBureau,
    isDownloadAvailable,
    isPremiumUser,
  } = props;
  const { ALL, TRANS_UNION, EXPERIAN, EQUIFAX } = BUREAU_ENUMS;
  return (
    <FlexContainer className={'width100 flex-column'}>
      <SixMonthPaymentHistoryRow showTilesRow showRow />
      <SixMonthPaymentHistoryRow
        data={parsedTradeline?.transunion?.status}
        bureau={'TransUnion'}
        showRow={selectedBureau === ALL || selectedBureau === TRANS_UNION}
      />
      <SixMonthPaymentHistoryRow
        data={parsedTradeline?.equifax?.status}
        bureau={'Equifax'}
        showRow={
          selectedBureau === ALL || selectedBureau === EQUIFAX || !isPremiumUser
        }
        unupdated={isDownloadAvailable || !isPremiumUser}
        linkClassName={'borderTopRadius8'}
      />
      <SixMonthPaymentHistoryRow
        data={parsedTradeline?.experian?.status}
        bureau={'Experian'}
        showRow={
          selectedBureau === ALL ||
          selectedBureau === EXPERIAN ||
          !isPremiumUser
        }
        unupdated={isDownloadAvailable || !isPremiumUser}
        linkClassName={'borderBottomRadius8'}
      />
    </FlexContainer>
  );
};

export const BureauPillAndRefreshButtonTableComponent = props => {
  const {
    is1B,
    transUnionBureauAnnotation,
    equifaxBureauAnnotation,
    experianBureauAnnotation,
    isDownloadAvailable,
    isPremiumUser,
    selectedBureau,
    isFreeUser,
  } = props;
  const showAllBureau = selectedBureau === ALL;
  const showRow = requiredBureau =>
    showAllBureau || selectedBureau === requiredBureau;
  const showFactorUpdateButton =
    isDownloadAvailable && !isFreeUser && selectedBureau !== TRANS_UNION;
  return (
    <FlexContainer className={'format40 flex-column margt19'}>
      <BureauPillRowConponent
        bureauAnnotation={transUnionBureauAnnotation}
        showRow={() => showRow(TRANS_UNION)}
        isDownloadAvailable={false}
      />
      <BureauPillRowConponent
        bureauAnnotation={equifaxBureauAnnotation}
        isDownloadAvailable={isDownloadAvailable}
        showRow={() =>
          showRow(EQUIFAX) && isPremiumUser && !isDownloadAvailable
        }
      />
      <BureauPillRowConponent
        bureauAnnotation={experianBureauAnnotation}
        isDownloadAvailable={isDownloadAvailable}
        showRow={() =>
          showRow(EXPERIAN) && isPremiumUser && !isDownloadAvailable
        }
      />
      {showFactorUpdateButton && (
        <FactorUpdateButton
          is1B={is1B}
          selectedBureau={selectedBureau}
          linkClassName={{ 'margt4 padtb10': selectedBureau === 'ALL' }}
        />
      )}
      {!isPremiumUser && <FactorAddDataButton />}
    </FlexContainer>
  );
};

export const BureauBalanceAndRefreshButtonTableComponent = props => {
  const {
    is1B,
    bureauBalance,
    isDownloadAvailable,
    isPremiumUser,
    selectedBureau,
  } = props;
  const showAllBureau = selectedBureau === ALL;
  const showLabel = requiredBureau =>
    showAllBureau || selectedBureau === requiredBureau;
  return (
    <FlexContainer className={'margt10 gap-10 align-items-end'}>
      <FlexContainer className={'flex-column format100 m-format80'}>
        <FlexContainer className={'flex-column padlr10 format100'}>
          {showLabel(TRANS_UNION) && (
            <Label className={'text-12'}>
              Transunion Balance:{' '}
              {bureauBalance?.transunion?.balance
                ? '$' + bureauBalance?.transunion?.balance
                : '$0'}
            </Label>
          )}
        </FlexContainer>
        <FlexContainer
          className={classnames('flex-column padlr10 format100 border-rad8px', {
            bgGray005: showAllBureau || !isPremiumUser,
          })}
        >
          {(showLabel(EQUIFAX) || !isPremiumUser) && (
            <Label
              className={classnames('text-12 padTB4', {
                system_neutral_n250_text: showAllBureau || !isPremiumUser,
              })}
            >
              Equifax Balance
              {bureauBalance?.equifax?.balance
                ? ': $' + bureauBalance?.equifax?.balance
                : ''}
            </Label>
          )}
          {(showLabel(EXPERIAN) || !isPremiumUser) && (
            <Label
              className={classnames('text-12 padTB4', {
                system_neutral_n250_text: showAllBureau || !isPremiumUser,
              })}
            >
              Experian Balance
              {bureauBalance?.experian?.balance
                ? ': $' + bureauBalance?.experian?.balance
                : ''}
            </Label>
          )}
        </FlexContainer>
      </FlexContainer>
      {isDownloadAvailable && selectedBureau !== TRANS_UNION && (
        <FactorUpdateButton
          is1B={is1B}
          selectedBureau={selectedBureau}
          linkClassName={'format30'}
          linkConditionalName={{ 'margt20 padtb4': selectedBureau === 'ALL' }}
        />
      )}
      {!isPremiumUser && (
        <FactorAddDataButton linkClassName={'format20 padTB4'} />
      )}
    </FlexContainer>
  );
};

export const TradelineNotification = props => {
  const { parsedTradeline, showLegend } = props;
  return (
    <FlexContainer
      className={classnames(
        'flex-container flex-column margt20 border-rad8px format100 pad16',
        parsedTradeline?.bgNotification,
      )}
    >
      <Label type="Body-2 mBody-2" className={'format100'}>
        {parsedTradeline?.notificationMessage}
      </Label>
      {showLegend && (
        <FlexContainer
          className={
            'bgWhite flex-column grayShadow16 border-rad8px margb16 pad12 gap-4'
          }
        >
          <FlexContainer className={'justify-between'}>
            <Label type="Body-2 mBody-2" className={'text-14'}>
              <span
                className={'text-7 padlr8 margr8 bgLegendBlue border-rad60px'}
              ></span>
              Suggested max usage (30%)
            </Label>
            <Label type="Body-2 mBody-2" className={'text-14 legendBlue'}>
              ${parsedTradeline?.creditLimit * 0.3}
            </Label>
          </FlexContainer>
          <FlexContainer className={'justify-between'}>
            <Label type="Body-2 mBody-2" className={'text-14'}>
              <span
                className={classnames('text-7 padlr8 margr8 border-rad60px', {
                  bgYellow100:
                    parsedTradeline?.duePercent >= 30 &&
                    parsedTradeline?.duePercent < 65,
                  bgOrange3:
                    parsedTradeline?.duePercent >= 65 &&
                    parsedTradeline?.duePercent < 85,
                  bgRed2: parsedTradeline?.duePercent >= 85,
                })}
              ></span>
              Over suggested usage by
            </Label>
            <Label
              type="Body-2 mBody-2"
              className={classnames('text-14', {
                fontRed100: parsedTradeline?.duePercent >= 85,
                fontOrange100:
                  parsedTradeline?.duePercent >= 65 &&
                  parsedTradeline?.duePercent < 85,
                system_warn_w400_text:
                  parsedTradeline?.duePercent >= 30 &&
                  parsedTradeline?.duePercent < 65,
              })}
            >
              ${parsedTradeline?.suggestedReductionValue}
            </Label>
          </FlexContainer>
        </FlexContainer>
      )}
    </FlexContainer>
  );
};

export const BureauAgeAndRefreshButtonTableComponent = props => {
  const {
    is1B,
    bureauAgeInMonths,
    isDownloadAvailable,
    isPremiumUser,
    selectedBureau,
  } = props;
  const showAllBureau = selectedBureau === ALL;
  const showLabel = requiredBureau =>
    showAllBureau || selectedBureau === requiredBureau;
  return (
    <FlexContainer className={'margt10 gap-10 align-items-end'}>
      <FlexContainer className={'flex-column format100 m-format80'}>
        <FlexContainer className={'flex-column padlr10 format100'}>
          {showLabel(TRANS_UNION) && (
            <Label className={'text-12'}>
              Transunion Account Age:{' '}
              {bureauAgeInMonths?.transunion?.ageInMonths
                ? monthsToYears(bureauAgeInMonths?.transunion?.ageInMonths)
                : '-'}
            </Label>
          )}
        </FlexContainer>
        <FlexContainer
          className={classnames('flex-column padlr10 format100 border-rad8px', {
            bgGray005: showAllBureau || !isPremiumUser,
          })}
        >
          {(showLabel(EQUIFAX) || !isPremiumUser) && (
            <Label
              className={classnames('text-12 padTB4', {
                system_neutral_n250_text: showAllBureau || !isPremiumUser,
              })}
            >
              Equifax Account Age:{' '}
              {bureauAgeInMonths?.equifax?.ageInMonths
                ? monthsToYears(bureauAgeInMonths?.equifax?.ageInMonths)
                : '-'}
            </Label>
          )}
          {(showLabel(EXPERIAN) || !isPremiumUser) && (
            <Label
              className={classnames('text-12 padTB4', {
                system_neutral_n250_text: showAllBureau || !isPremiumUser,
              })}
            >
              Experian Account Age:{' '}
              {bureauAgeInMonths?.experian?.ageInMonths
                ? monthsToYears(bureauAgeInMonths?.experian?.ageInMonths)
                : '-'}
            </Label>
          )}
        </FlexContainer>
      </FlexContainer>
      {isDownloadAvailable && selectedBureau !== TRANS_UNION && (
        <FactorUpdateButton
          is1B={is1B}
          selectedBureau={selectedBureau}
          linkClassName={'format20'}
          linkConditionalName={{ 'margt20 padtb4': selectedBureau === 'ALL' }}
        />
      )}
      {!isPremiumUser && (
        <FactorAddDataButton linkClassName={'format20 padTB4'} />
      )}
    </FlexContainer>
  );
};

export const TradelineHeader = props => {
  const {
    titleStyle,
    title,
    subtitle,
    basedOnBureauLabel,
    accountAmmount,
    statusLabel,
  } = props;
  return (
    <FlexContainer className={'justify-between'}>
      <FlexContainer className={'flex-column'}>
        <Label type="Subtitle-2" className={titleStyle()}>
          {title}
        </Label>
        <Label type="Other" className={'system_neutral_n250_text text-12'}>
          {subtitle}
        </Label>
        <Label className={'system_neutral_n250_text italic text-12'}>
          {basedOnBureauLabel}
        </Label>
      </FlexContainer>
      <FlexContainer className={'flex-column'}>
        <Label
          type="Subtitle-2"
          className={'bold align-self-end system_neutral_n250_text'}
        >
          {accountAmmount}
        </Label>
        <Label
          type="Other"
          className={'align-self-end system_neutral_n250_text text-12'}
        >
          {statusLabel}
        </Label>
      </FlexContainer>
    </FlexContainer>
  );
};
